export type RegionDef = {
  id: string;
  label: string;
};

/** NL grid regions — matches freq-service config. */
export const NL_REGIONS: RegionDef[] = [
  { id: "NL_GR", label: "Groningen / Drenthe / Overijssel" },
  { id: "NL_GE", label: "Gelderland / Flevoland" },
  { id: "NL_NH", label: "Noord-Holland" },
  { id: "NL_ZE", label: "Zeeland" },
  { id: "NL_NB", label: "Brabant" },
  { id: "NL_LI", label: "Limburg" },
  { id: "NL_ZH", label: "Zuid-Holland" },
  { id: "NL_FR", label: "Friesland" },
  { id: "NL_UT", label: "Utrecht" },
];

export const REGIONAL_RANGE_MINUTES = [1, 5, 15, 60] as const;
export type RegionalRangeMinutes = (typeof REGIONAL_RANGE_MINUTES)[number];

/** Max chart horizon and SQLite / in-memory retention (must match server). */
export const REGIONAL_MAX_HISTORY_MINUTES = 60;
export const REGIONAL_MAX_RETENTION_MS = REGIONAL_MAX_HISTORY_MINUTES * 60 * 1000;

/** ECG tab: fast first paint — longer ranges fetched when selected. */
export const ECG_INITIAL_HISTORY_MINUTES = 1 satisfies RegionalRangeMinutes;

export const REGIONAL_AVG_SECONDS = [5, 10, 20, 30] as const;
export type RegionalAvgSeconds = (typeof REGIONAL_AVG_SECONDS)[number];
export type RegionalAvgChoice = "off" | RegionalAvgSeconds;

export function avgChoiceToTauMs(choice: RegionalAvgChoice): number | null {
  return choice === "off" ? null : choice * 1000;
}

export const MAIN_FREQ_CHART = {
  id: "freq/main",
  label: "Mains frequency (authoritative)",
} as const;

/** ECG single-line chart height. */
export const ECG_FREQ_CHART_HEIGHT_PX = 168;

/** Ribbon line colors — mains darker, regions match /frequency mains line. */
export const FREQ_MAINS_LINE_COLOR = "#38bdf8";
export const ECG_MAINS_RIBBON_COLOR = "#003d99";
export const ECG_REGION_RIBBON_COLOR = FREQ_MAINS_LINE_COLOR;

/** Default spacing between NL regions 2–9 (millihertz); mains→first region is fixed. */
export const ECG_RIBBON_SHIFT_MHZ_DEFAULT = 10;
export const ECG_RIBBON_SHIFT_MHZ_MAX = 20;

/** Fixed vertical gap from freq/main down to the first NL region (millihertz). */
export const ECG_RIBBON_MAINS_TO_FIRST_MHZ = 20;

/** Y-offset (Hz) added per ribbon slot — mains on top, then fixed 20 mHz, then slider gaps. */
export function ribbonLineOffsetHz(slot: number, regionGapMhz: number): number {
  const regionGapHz = Math.max(0, regionGapMhz) / 1000;
  const mainsToFirstHz = ECG_RIBBON_MAINS_TO_FIRST_MHZ / 1000;
  const n = NL_REGIONS.length;
  if (slot === 0) return mainsToFirstHz + (n - 1) * regionGapHz;
  return (n - slot) * regionGapHz;
}

/** Expected regional meter count for ECG chart titles (3 ± surplus/deficit). */
export const REGIONAL_METER_TARGET = 3;

export const METER_TRACE_COLORS = [
  "#94a3b8",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#14b8a6",
] as const;
